import { useState, useEffect } from 'react'

import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Paper from '@mui/material/Paper'
import Table from '@mui/material/Table'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import TableCell from '@mui/material/TableCell'
import TableBody from '@mui/material/TableBody'
import AutocompleteSelect from '../../../common/components/AutocompleteSelect'
import Alert from '@mui/material/Alert'

interface Subsidiary {
    id: string | number
    name: string
}
interface Customer {
    id: string | number
    name: string
}
interface Account {
    id: string | number
    name: string
    type: string
}

interface Props {
    settings: Record<string, unknown>
    shops: Array<{ id: number; shop_name: string }>
    subsidiaries: Subsidiary[]
    customers: Customer[]
    accounts: Account[]
    setSettings: React.Dispatch<React.SetStateAction<Record<string, unknown>>>
}

export default function Connections({ settings, shops, subsidiaries, customers, accounts, setSettings }: Props) {
    type ShopRow = {
        shop_id: number
        subsidiary_id: string
        customer_id: string
        clearing_account: string
        bank_account: string
        inbound_stock_adjustment: string
    }

    const subsidiaryOptions: [string, string][] = subsidiaries.map((s) => [s.name, String(s.id)])
    const customerOptions: [string, string][] = customers.map((c) => [c.name, String(c.id)])
    const allAccountOptions: [string, string][] = accounts.map((a) => [a.name, String(a.id)])
    const bankAccountOptions: [string, string][] = accounts
        .filter((a) => a.type === 'Bank') // keep only bank-type accounts
        .map((a) => [a.name, String(a.id)])

    // Build lookup from existing shop_map if present
    const buildShopMap = (): Record<number, Partial<ShopRow>> => {
        const map: Record<number, Partial<ShopRow>> = {}
        ;((settings.shop_map as unknown[]) || []).forEach((item) => {
            if (item && typeof item === 'object' && 'shop_id' in item) {
                const entry = item as Record<string, string | number>
                map[entry.shop_id as number] = {
                    subsidiary_id: String(entry.subsidiary_id ?? ''),
                    customer_id: String(entry.customer_id ?? ''),
                    clearing_account: String(entry.clearing_account ?? ''),
                    bank_account: String(entry.bank_account ?? ''),
                    inbound_stock_adjustment: String(entry.inbound_stock_adjustment ?? ''),
                }
            }
        })
        return map
    }

    const buildRows = (shopMap: Record<number, Partial<ShopRow>>): ShopRow[] =>
        shops.map((shop) => ({
            shop_id: shop.id,
            subsidiary_id: shopMap[shop.id]?.subsidiary_id ?? '',
            customer_id: shopMap[shop.id]?.customer_id ?? '',
            clearing_account: shopMap[shop.id]?.clearing_account ?? '',
            bank_account: shopMap[shop.id]?.bank_account ?? '',
            inbound_stock_adjustment: shopMap[shop.id]?.inbound_stock_adjustment ?? '',
        }))

    const [rows, setRows] = useState<ShopRow[]>(() => buildRows(buildShopMap()))

    useEffect(() => {
        const newRows = buildRows(buildShopMap())
        setRows(newRows)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [settings.shop_map, shops])

    const handleChange = (
        idx: number,
        field: keyof Omit<ShopRow, 'shop_id'>,
        value: string
    ) => {
        setRows((prev) => {
            const updated = prev.map((row, i) => (i === idx ? { ...row, [field]: value } : row))
            setSettings((s: Record<string, unknown>) => ({ ...s, shop_map: updated }))
            return updated
        })
    }

    const renderSelect = (
        idx: number,
        field: keyof Omit<ShopRow, 'shop_id'>,
        options: [string, string][]
    ) => {
        const optionObjs = options.map(([label, value]) => ({ label, value }))
        return (
            <AutocompleteSelect
                options={optionObjs}
                value={rows[idx][field]}
                onChange={(val) => handleChange(idx, field, String(val))}
            />
        )
    }

    const hasShops = rows.length > 0

    return (
        <Box sx={{ p: 2 }}>
            {!hasShops ? (
                <Alert
                    severity="info"
                    variant="outlined"
                    sx={{
                        color: 'text.secondary',
                        bgcolor: 'grey.100',
                        borderColor: 'grey.300',
                        '& .MuiAlert-icon': { color: 'text.secondary' },
                    }}
                >
                    No shops available
                </Alert>
            ) : (
                <Box sx={{ mt: 1, overflowX: 'auto' }}>
                        <Table size="small" sx={{ minWidth: 1260, border: 'none' }}>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 'bold', py: 0.75, px: 1, fontSize: '0.8125rem', width: 180, maxWidth: 180, position: 'sticky', left: 0, zIndex: 3, bgcolor: 'background.paper' }}>
                                    Connection
                                </TableCell>
                            <TableCell sx={{ fontWeight: 'bold', py: 0.75, px: 1, fontSize: '0.8125rem', width: 180, maxWidth: 180 }}>Subsidiary</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', py: 0.75, px: 1, fontSize: '0.8125rem', width: 180, maxWidth: 180 }}>Customer</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', py: 0.75, px: 1, fontSize: '0.8125rem', width: 180, maxWidth: 180 }}>Clearing Account</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', py: 0.75, px: 1, fontSize: '0.8125rem', width: 180, maxWidth: 180 }}>Bank Account</TableCell>
                                                        <TableCell sx={{ fontWeight: 'bold', py: 0.75, px: 1, fontSize: '0.8125rem', width: 180, maxWidth: 180 }}>Inbound Stock Adjustment</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rows.map((row, idx) => (
                            <TableRow
                                key={row.shop_id}
                                sx={{ bgcolor: idx % 2 ? 'grey.50' : 'common.white' }}
                            >
                                <TableCell sx={{ lineHeight: '1', position: 'sticky', left: 0, zIndex: 2, bgcolor: idx % 2 ? 'grey.50' : 'common.white' }}>
                                    <Typography variant="body2" sx={{ fontSize: '0.8125rem' }}>{shops[idx].shop_name}</Typography>
                                </TableCell>
                                <TableCell sx={{ width: 180, maxWidth: 180, p: 1 }}>
                                    {renderSelect(idx, 'subsidiary_id', subsidiaryOptions)}
                                </TableCell>
                                <TableCell sx={{ width: 180, maxWidth: 180, p: 1 }}>
                                    {renderSelect(idx, 'customer_id', customerOptions)}
                                </TableCell>
                                <TableCell sx={{ width: 180, maxWidth: 180, p: 1 }}>
                                    {renderSelect(idx, 'clearing_account', allAccountOptions)}
                                </TableCell>
                                <TableCell sx={{ width: 180, maxWidth: 180, p: 1 }}>
                                    {renderSelect(idx, 'bank_account', bankAccountOptions)}
                                </TableCell>
                                                                <TableCell sx={{ width: 180, maxWidth: 180, p: 1 }}>
                                    {renderSelect(idx, 'inbound_stock_adjustment', allAccountOptions)}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
                    </Box>
            )}
        </Box>
    )
}
